import { Building, Building2, FileSpreadsheet, FileText, Globe, Handshake, LineChart, UserRound } from "lucide-react";
import { cn } from "@/lib/utils";
import { Aparece } from "./Aparece";
import { MODULOS, PORTALES_PUBLICACION } from "./content";
import { Pill } from "./mockups/MarcoApp";
import { Proximamente } from "./Proximamente";

// Bento asimétrico: cada celda muestra un FRAGMENTO de la app. Si una celda terminara en ícono +
// título + párrafo, sería la grilla de 15 cards iguales de la competencia con otro layout.
// Las celdas no son clickeables, así que no tienen hover: un hover promete una acción.

const Frag = ({ className, children }) => (
  <div className={cn("mt-5 rounded-xl bg-slate-50/80 p-3 ring-1 ring-inset ring-slate-200/60", className)} aria-hidden="true">
    {children}
  </div>
);

const FragPropiedades = () => (
  <Frag>
    {[
      ["Depto. 1204 · Providencia", "Contrato hasta Mar 2027", "Arrendada", "pagado"],
      ["Casa 18 · La Reina", "Disponible desde 1 Oct", "Vacante", "neutro"],
      ["Depto. 804 · Vitacura", "Publicada en venta", "En venta", "indigo"],
    ].map(([p, d, e, t], i) => (
      <div key={p} className={cn("flex items-center justify-between gap-3 py-2 text-xs", i > 0 && "border-t border-slate-200/70")}>
        <div className="min-w-0">
          <p className="truncate font-semibold text-slate-800">{p}</p>
          <p className="text-[11px] text-slate-500">{d}</p>
        </div>
        <Pill tono={t}>{e}</Pill>
      </div>
    ))}
  </Frag>
);

const FragPortales = () => (
  <Frag>
    <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Estado de cuenta · M. González</p>
    {[
      ["Liquidación Agosto", "Pagada", "pagado"],
      ["Liquidación Septiembre", "Enviada", "indigo"],
    ].map(([l, e, t]) => (
      <div key={l} className="mt-2 flex items-center justify-between gap-2 text-xs">
        <span className="flex items-center gap-1.5 text-slate-700"><FileText className="h-3.5 w-3.5 text-slate-400" />{l}</span>
        <Pill tono={t}>{e}</Pill>
      </div>
    ))}
  </Frag>
);

const BARRAS = [62, 70, 68, 81, 88, 94];
const FragReportes = () => (
  <Frag>
    <div className="flex h-16 items-end gap-1.5">
      {BARRAS.map((h, i) => (
        <div key={i} className={cn("flex-1 rounded-t-md", i === BARRAS.length - 1 ? "bg-indigo-500" : "bg-indigo-200")} style={{ height: `${h}%` }} />
      ))}
    </div>
    <div className="mt-3 flex items-center justify-between">
      <span className="text-[11px] font-semibold text-slate-600">Ocupación · 6 meses</span>
      <span className="flex gap-1.5"><Pill tono="pagado"><FileSpreadsheet className="mr-1 h-3 w-3" />Excel</Pill><Pill tono="atraso"><FileText className="mr-1 h-3 w-3" />PDF</Pill></span>
    </div>
  </Frag>
);

const FragCondominios = () => (
  <Frag>
    <p className="text-xs font-semibold text-slate-800">Edificio Los Aromos</p>
    <p className="text-[11px] text-slate-500">24 unidades · Admite mascotas</p>
    <div className="mt-2 flex flex-wrap gap-1.5">
      <Pill tono="indigo">Conserjería</Pill><Pill tono="neutro">Gasto común</Pill>
    </div>
  </Frag>
);

const FragPublicacion = () => (
  <Frag>
    {PORTALES_PUBLICACION.map((p, i) => (
      <div key={p.nombre} className={cn("flex items-center justify-between py-1.5 text-xs", i > 0 && "border-t border-slate-200/70")}>
        <span className={cn("flex items-center gap-1.5 font-semibold", p.disponible ? "text-slate-700" : "text-slate-500")}>
          <Globe className="h-3.5 w-3.5 text-slate-400" />{p.nombre}
        </span>
        {p.disponible ? <Pill tono="pagado">Publicada</Pill> : <Pill tono="neutro">Próximamente</Pill>}
      </div>
    ))}
  </Frag>
);

const FragVentas = () => (
  <Frag className="sm:mt-0">
    <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Venta · Depto. 804</p>
    <dl className="mt-2 grid grid-cols-3 gap-3 text-xs">
      {[
        ["Precio", "UF 4.200"],
        ["Comisión 2%", "UF 84"],
        ["Neto propietario", "UF 4.116"],
      ].map(([k, v]) => (
        <div key={k}>
          <dt className="text-[11px] text-slate-500">{k}</dt>
          <dd className="font-display font-extrabold tabular-nums text-slate-900">{v}</dd>
        </div>
      ))}
    </dl>
  </Frag>
);

const CELDAS = {
  propiedades: { icono: Building2, frag: FragPropiedades, clase: "lg:col-span-2 border-l-4 border-l-indigo-500" },
  portales: { icono: UserRound, frag: FragPortales },
  reportes: { icono: LineChart, frag: FragReportes },
  condominios: { icono: Building, frag: FragCondominios },
  publicacion: { icono: Globe, frag: FragPublicacion },
  ventas: { icono: Handshake, frag: FragVentas, clase: "lg:col-span-3 sm:grid sm:grid-cols-[1fr_1.2fr] sm:items-center sm:gap-8" },
};

export const Modulos = () => (
  <section id="producto" className="scroll-mt-16 bg-white" data-testid="landing-modulos">
    <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:py-28">
      <Aparece className="max-w-2xl">
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Producto</p>
        <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
          Todo lo que pasa en tu cartera. <span className="text-slate-400">En un solo lugar.</span>
        </h2>
      </Aparece>

      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {MODULOS.map((m, i) => {
          const c = CELDAS[m.key];
          const Icono = c.icono;
          const Fragmento = c.frag;
          return (
            <Aparece
              key={m.key}
              delay={(i % 3) * 0.05}
              className={cn(
                "rounded-2xl bg-white p-6 shadow-sm ring-1 ring-inset ring-slate-200/60",
                m.key === "propiedades" && "sm:col-span-2",
                m.key === "ventas" && "sm:col-span-2",
                c.clase,
              )}
              data-testid={`modulo-${m.key}`}
            >
              <div>
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50">
                  <Icono className="h-5 w-5 text-indigo-600" />
                </span>
                <h3 className="mt-4 flex flex-wrap items-center gap-2 text-base font-bold text-slate-800">
                  {m.titulo}
                  {m.proximamente && <Proximamente />}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{m.texto}</p>
              </div>
              <Fragmento />
            </Aparece>
          );
        })}
      </div>
    </div>
  </section>
);
